/**
 * Category form → "Available only at these times" (F-DEAL-N2). Saved on its
 * own button, separate from the category's main save.
 */
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toastHelpers } from '@/lib/toast-helpers'
import { WindowListEditor } from './DealsControls'
import { errorText, windowProblem, type DraftWindow } from './dealsText'

export function CategoryHoursSection({ categoryId }: { categoryId: string }) {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ['category-availability', categoryId],
    queryFn: async () => (await apiClient.getCategoryAvailability(categoryId)).data?.availability ?? [],
  })
  const [windows, setWindows] = useState<DraftWindow[]>([])
  const [baseline, setBaseline] = useState('[]')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!query.data) return
    const ws = query.data.map((w) => ({ days: [...(w.days ?? [])], start_minute: w.start_minute, end_minute: w.end_minute }))
    setWindows(ws)
    setBaseline(JSON.stringify(ws))
  }, [query.data])

  const dirty = JSON.stringify(windows) !== baseline

  const save = async () => {
    setError(null)
    for (const w of windows) {
      const p = windowProblem(w)
      if (p) return setError(p)
    }
    setSaving(true)
    try {
      await apiClient.putCategoryAvailability(
        categoryId,
        windows.map((w) => ({ days: w.days.length === 7 ? [] : w.days, start_minute: w.start_minute as number, end_minute: w.end_minute as number })),
      )
      await qc.invalidateQueries({ queryKey: ['category-availability', categoryId] })
      qc.invalidateQueries({ queryKey: ['menu-rules'] })
      toastHelpers.success('Hours saved')
    } catch (e) {
      setError(errorText(e, 'Could not save hours'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="text-sm font-medium">Available only at these times</p>
        <p className="text-xs text-muted-foreground">For example breakfast items 8 AM – 12 PM. Items outside their hours cannot be added on the till or online.</p>
      </div>
      {query.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : query.isError ? (
        <p className="text-sm text-destructive">Could not load this category’s hours.</p>
      ) : (
        <>
          <WindowListEditor value={windows} onChange={setWindows} emptyText="No limits — items here can be sold whenever you are open." />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="button" size="sm" variant={dirty ? 'default' : 'outline'} disabled={!dirty || saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save hours'}
          </Button>
        </>
      )}
    </div>
  )
}
